import { useState, useContext, useEffect } from "react";
import landingStyle from "../styles/landing.module.scss";
import LoginFunction from "../utilities/LoginFunction";
import { UserContext } from "../context/UserContext";
import useToken from "../utilities/hooks/useToken";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { postLoginLabel, safePostLoginPath } from "../utilities/postLoginPath";
import { consumeSignInRenewal, hasValidToken } from "../utilities/AuthState";
import {
  accountNeedsPassword,
  canComparePasswordLocally,
  getAccountId,
  getStoredAccountEmail,
  getAccountUserType,
  identifierLooksLikeEmail,
  loginErrorMessage,
} from "../utilities/loginAccount";

const ASIATAN_LOGO = "https://matlib.co/images/asiatanLogo.png";
const TECTUFF_LOGO = "https://matlib.co/images/tectuffLogo.png";

function LoginForm() {
  const { changeUserInformation, userInformation, changelogOutUser } =
    useContext(UserContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const { setToken } = useToken();
  const [message, setMessage] = useState("");
  const [renewalNotice, setRenewalNotice] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safePostLoginPath(searchParams.get("next"));
  const nextLabel = postLoginLabel(nextPath);

  const handleLogOutUser = () => {
    changelogOutUser();
  };

  const normalizeLoginIdentifier = (value) => value.trim().toLowerCase();

  const completeLogin = (response, loginIdentifier = "") => {
    const accessToken =
      response.accessToken || response.token || response.access_token;

    if (hasValidToken(accessToken)) {
      setToken(accessToken);
    }

    changeUserInformation({
      userId: getAccountId(response),
      userType: response.userType,
      vendor: response.brand,
      userName: response.userName,
      email: getStoredAccountEmail(response, loginIdentifier),
      isLoggedIn: true,
    });
    navigate(nextPath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loginIdentifier = normalizeLoginIdentifier(email);
    const isEmailLogin = identifierLooksLikeEmail(loginIdentifier);

    if (!loginIdentifier) {
      setMessage("Enter your email or ID");
      return;
    }

    if (isEmailLogin && !password) {
      setMessage("Enter your password");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await LoginFunction(
        loginIdentifier,
        password || undefined,
      );

      if (!getAccountId(response)) {
        setMessage("account not found");
        return;
      }

      setUserInfo(response);

      const needsPassword = isEmailLogin || accountNeedsPassword(response);
      if (needsPassword && !password) {
        setMessage("Enter your password");
        return;
      }

      if (
        password &&
        canComparePasswordLocally(response.password) &&
        String(password) !== String(response.password)
      ) {
        setMessage("password incorrect");
        return;
      }

      completeLogin(response, loginIdentifier);
    } catch (error) {
      setMessage(loginErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const inputField = (e) => {
    setEmail(e.target.value);
    setMessage("");
  };
  const passwordField = (e) => {
    setPassword(e.target.value);
    setMessage("");
  };

  const { isLoggedIn, userName } = userInformation;

  useEffect(() => {
    if (isLoggedIn) {
      return;
    }
    if (consumeSignInRenewal()) {
      setRenewalNotice(true);
    }
  }, [isLoggedIn]);
  const showPasswordField =
    identifierLooksLikeEmail(email) ||
    getAccountUserType(userInfo) === "vendor" ||
    Boolean(userInfo?.password);

  if (userName && isLoggedIn) {
    return (
      <div className={landingStyle.sessionPanel}>
        <div className={landingStyle.vendorLogos}>
          <img src={ASIATAN_LOGO} alt="asiatan" />
          <img src={TECTUFF_LOGO} alt="tectuff" />
        </div>
        <div className={landingStyle.loginViewContent}>
          <p className={landingStyle.stateLabel}>Current Session</p>
          <h3 className={landingStyle.loginViewHeading}>
            You are currently logged in as {userInformation.userName} from{" "}
            {userInformation.vendor}
          </h3>
          <div className={landingStyle.sessionActionGroup}>
            <button
              className="button-highlight"
              onClick={() => navigate(nextPath)}
            >
              {nextLabel ? `open ${nextLabel}` : "go to Library"}
            </button>
            <button className="button-highlight" onClick={handleLogOutUser}>
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={landingStyle.loginView}>
      <div className={landingStyle.vendorLogos}>
        <img src={ASIATAN_LOGO} alt="asiatan" />
        <img src={TECTUFF_LOGO} alt="tectuff" />
      </div>
      <form className={landingStyle.loginForm} onSubmit={handleSubmit}>
        <div className={landingStyle.formHeader}>
          <p className={landingStyle.stateLabel}>Sign In</p>
          <h3>Welcome back</h3>
          <p>
            {renewalNotice
              ? "Your sign-in needs to be renewed."
              : nextLabel
                ? `After sign in you will open ${nextLabel}.`
                : "Sign in with your email or ID."}
          </p>
        </div>

        <div className={landingStyle.loginInputGroup}>
          <label htmlFor="email">Email or ID</label>
          <input
            id="email"
            name="email"
            type="text"
            placeholder="Email or ID"
            autoComplete="username"
            value={email}
            onChange={inputField}
          />
          {showPasswordField ? (
            <>
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="Enter password"
                autoComplete="current-password"
                value={password}
                onChange={passwordField}
              />
            </>
          ) : null}
          {message !== "" ? (
            <div className={landingStyle.loginErrorMessage}>{message}</div>
          ) : null}
        </div>
        {loading ? (
          <div className={landingStyle.loginLoading} aria-live="polite">
            <div className={landingStyle.loadingDots} aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className={landingStyle.loadingLabel}>Checking access</span>
          </div>
        ) : null}
        <button
          className={`${landingStyle.button} ${!email.trim() ? landingStyle.buttonDisabled : ""}`}
          type="submit"
          disabled={!email.trim()}
        >
          {loading ? "Signing in..." : "Login"}
        </button>
        <Link className={landingStyle.textLink} to="/forgot-password">
          Forgot password?
        </Link>
      </form>
    </div>
  );
}

export default LoginForm;
